import { useCallback, useEffect, useRef, useState } from "react";
import { IconCamera, IconClose, IconFlash, IconKeyboard } from "../icons";
import {
  abrirCamera,
  carregarDetector,
  chaveDoCodigo,
  criarSons,
  iniciarLeitura,
  MIRA,
  motivoSemCamera,
  type CameraAberta,
} from "../leitor-camera";

/**
 * PAINEL DA CÂMERA na conferência de material.
 *
 * Fica DENTRO do card do modal, nunca por cima dele: o fundo escuro do modal
 * fecha a conferência ao primeiro toque, e um painel solto ali em cima faria a
 * pessoa perder a conferência inteira tentando mirar o frasco.
 *
 * O modo é CONTÍNUO: aponta e vai bipando, sem tocar na tela entre um frasco e
 * outro. É o que aproxima a câmera do leitor de mão. Duas regras seguram o
 * efeito colateral disso:
 *
 *  1. o mesmo código lido de novo dentro de 2,5 s é IGNORADO em silêncio (a
 *     câmera lê o mesmo frasco umas 8 vezes por segundo — sem isso a tela
 *     piscaria "já conferido" sem parar);
 *  2. só a faixa central da imagem é lida (a mira), para não conferir o frasco
 *     vizinho por engano — ver src/leitor-camera.ts.
 *
 * O código lido aparece GIGANTE: é a conferência humana. Câmera lê errado de
 * vez em quando; quem está com o frasco na mão percebe na hora se o número não
 * é aquele.
 */
/**
 * O que a leitura deu. "alerta" é o item que ENTROU, mas com algo a conferir
 * (equipamento de ensaio com calibração vencida, liberado para outra OS...):
 * tom amarelo e o som de "já tinha", para a pessoa olhar a tela sem achar que
 * foi recusado. Quem não usa (conferência de material, seringas) nunca o devolve.
 */
export type ResultadoLeitura = "novo" | "duplicado" | "erro" | "alerta";

interface Props {
  /** Códigos que já estão na remessa (chave normalizada), para avisar sem ida ao servidor. */
  jaConferidos: Set<string>;
  /** Registra o código. Devolve o que aconteceu, para o som certo tocar. */
  onCodigo: (texto: string) => Promise<ResultadoLeitura>;
  /** Fecha o painel (a câmera é desligada de qualquer jeito). */
  onFechar: () => void;
  /** Fecha o painel e devolve o foco ao campo de digitar. */
  onDigitar: () => void;
}

/** Silêncio entre leituras do MESMO código, em milissegundos. */
const JANELA_SILENCIO = 2500;
/** Sem nenhuma leitura por este tempo, a câmera pausa sozinha (bateria/calor). */
const PAUSA_AUTOMATICA = 45_000;

export default function CameraScanner({ jaConferidos, onCodigo, onFechar, onDigitar }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const cameraRef = useRef<CameraAberta | null>(null);
  const pararRef = useRef<(() => void) | null>(null);
  const sonsRef = useRef<ReturnType<typeof criarSons> | null>(null);
  const vistosRef = useRef<Map<string, number>>(new Map());
  const ultimaLeituraRef = useRef<number>(Date.now());
  const wakeLockRef = useRef<any>(null);
  /** Trava para não mandar duas leituras ao servidor ao mesmo tempo. */
  const enviandoRef = useRef(false);

  const [estado, setEstado] = useState<"abrindo" | "lendo" | "pausada" | "erro">("abrindo");
  const [erro, setErro] = useState<string | null>(null);
  const [ultimo, setUltimo] = useState<{ texto: string; tom: ResultadoLeitura } | null>(null);
  const [temLanterna, setTemLanterna] = useState(false);
  const [lanterna, setLanterna] = useState(false);
  const [dica, setDica] = useState<string | null>(null);

  /** Desliga tudo — chamado ao fechar o painel e quando o componente sai da tela. */
  const desligar = useCallback(() => {
    pararRef.current?.();
    pararRef.current = null;
    cameraRef.current?.fechar();
    cameraRef.current = null;
    sonsRef.current?.fechar();
    sonsRef.current = null;
    wakeLockRef.current?.release?.().catch?.(() => {});
    wakeLockRef.current = null;
  }, []);

  /** Uma leitura do decodificador: decide se manda ao servidor, ignora ou avisa. */
  const tratarLeitura = useCallback(
    async (texto: string) => {
      const chave = chaveDoCodigo(texto);
      if (!chave) return;

      const agora = Date.now();
      ultimaLeituraRef.current = agora;
      setDica(null);

      // Mesmo código lido de novo em seguida: a câmera está parada nele. Silêncio.
      const visto = vistosRef.current.get(chave);
      if (visto && agora - visto < JANELA_SILENCIO) return;
      vistosRef.current.set(chave, agora);

      // Já está na remessa: avisa sem incomodar o servidor.
      if (jaConferidos.has(chave)) {
        sonsRef.current?.duplicado();
        setUltimo({ texto, tom: "duplicado" });
        return;
      }

      if (enviandoRef.current) return;
      enviandoRef.current = true;
      try {
        const resultado = await onCodigo(texto);
        setUltimo({ texto, tom: resultado });
        if (resultado === "novo") sonsRef.current?.novo();
        else if (resultado === "duplicado" || resultado === "alerta") sonsRef.current?.duplicado();
        else sonsRef.current?.recusado();
      } finally {
        enviandoRef.current = false;
      }
    },
    [jaConferidos, onCodigo],
  );

  /** Abre a câmera e começa a ler. Também é o botão "Retomar". */
  const começar = useCallback(async () => {
    setErro(null);
    setEstado("abrindo");
    try {
      // Os sons nascem aqui, dentro do toque da pessoa: no iPhone, áudio criado
      // fora de um gesto é bloqueado e a conferência ficaria muda.
      sonsRef.current ??= criarSons();

      const detector = await carregarDetector();
      const video = videoRef.current;
      if (!video) return;

      cameraRef.current ??= await abrirCamera(video);
      setTemLanterna(cameraRef.current.temLanterna);

      // Mantém a tela acesa enquanto se confere a caixa (onde existir).
      try {
        wakeLockRef.current ??= await (navigator as any).wakeLock?.request("screen");
      } catch {
        /* opcional */
      }

      ultimaLeituraRef.current = Date.now();
      pararRef.current?.();
      pararRef.current = iniciarLeitura(video, detector, (achado) => {
        void tratarLeitura(achado.texto);
      });
      setEstado("lendo");
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Não foi possível abrir a câmera.");
      setEstado("erro");
    }
  }, [tratarLeitura]);

  useEffect(() => {
    void começar();
    return desligar;
    // Só na montagem: reabrir a câmera a cada render derrubaria a leitura.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Pausa sozinha depois de um tempo sem ler nada, e dá dicas antes disso. */
  useEffect(() => {
    if (estado !== "lendo") return;
    const timer = setInterval(() => {
      const parada = Date.now() - ultimaLeituraRef.current;
      if (parada > PAUSA_AUTOMATICA) {
        pararRef.current?.();
        pararRef.current = null;
        setEstado("pausada");
      } else if (parada > 10_000) {
        setDica(
          temLanterna && !lanterna
            ? "Não está lendo? Ligue a lanterna, limpe a lente ou digite o código."
            : "Não está lendo? Aproxime até a etiqueta preencher a mira, evite o reflexo — ou digite o código.",
        );
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [estado, temLanterna, lanterna]);

  /** Some com a câmera assim que a aba sai de vista (bateria e privacidade). */
  useEffect(() => {
    const aoTrocarAba = () => {
      if (document.hidden && estado === "lendo") {
        pararRef.current?.();
        pararRef.current = null;
        setEstado("pausada");
      }
    };
    document.addEventListener("visibilitychange", aoTrocarAba);
    return () => document.removeEventListener("visibilitychange", aoTrocarAba);
  }, [estado]);

  async function alternarLanterna() {
    const nova = !lanterna;
    await cameraRef.current?.ligarLanterna(nova);
    setLanterna(nova);
  }

  function fechar() {
    desligar();
    onFechar();
  }

  const impedimento = motivoSemCamera();

  return (
    <div className="conf-cam">
      <div className="conf-cam-topo">
        <strong>
          <IconCamera size={16} /> Leitura pela câmera
        </strong>
        <span className="muted">
          {estado === "lendo"
            ? "aponte a etiqueta para a faixa — vai bipando sozinho"
            : estado === "abrindo"
              ? "preparando a câmera…"
              : estado === "pausada"
                ? "pausada"
                : "câmera indisponível"}
        </span>
        <button type="button" className="icon-btn" onClick={fechar} title="Fechar a câmera">
          <IconClose size={18} />
        </button>
      </div>

      {impedimento || erro ? (
        <div className="conf-aviso erro conf-cam-erro">
          {impedimento ?? erro}
          <div className="conf-cam-botoes">
            {!impedimento && (
              <button type="button" className="btn-secondary" onClick={() => void começar()}>
                Tentar de novo
              </button>
            )}
            <button type="button" className="btn-primary" onClick={onDigitar}>
              <IconKeyboard size={16} /> Digitar o código
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="conf-cam-quadro">
            <video ref={videoRef} className="conf-cam-video" playsInline muted autoPlay />
            {/* A mira: é exatamente a área que o leitor enxerga. */}
            <div
              className="conf-cam-mira"
              style={{ width: `${MIRA.largura * 100}%`, height: `${MIRA.altura * 100}%` }}
            />
            {estado === "pausada" && (
              <div className="conf-cam-pausa">
                <p>Câmera pausada para poupar a bateria.</p>
                <button type="button" className="btn-primary" onClick={() => void começar()}>
                  <IconCamera size={16} /> Retomar leitura
                </button>
              </div>
            )}
          </div>

          {/* O código lido, grande: é a conferência de quem está com o frasco na mão. */}
          {/* "alerta" usa a cor do duplicado (amarelo) de propósito: é a mesma
              mensagem para os olhos — entrou, mas olhe a tela. */}
          <div className={`conf-cam-lido ${ultimo?.tom === "alerta" ? "duplicado" : (ultimo?.tom ?? "")}`}>
            {ultimo ? (
              <>
                <span className="conf-cam-codigo">{ultimo.texto}</span>
                <span className="conf-cam-tom">
                  {ultimo.tom === "novo"
                    ? "conferido"
                    : ultimo.tom === "duplicado"
                      ? "já estava na lista"
                      : ultimo.tom === "alerta"
                        ? "entrou — confira o aviso"
                        : "não entrou"}
                </span>
              </>
            ) : (
              <span className="muted">nenhuma leitura ainda</span>
            )}
          </div>

          {dica && <div className="conf-aviso atencao">{dica}</div>}

          <div className="conf-cam-botoes">
            {temLanterna && (
              <button
                type="button"
                className={lanterna ? "btn-primary" : "btn-secondary"}
                onClick={() => void alternarLanterna()}
              >
                <IconFlash size={16} /> {lanterna ? "Desligar lanterna" : "Lanterna"}
              </button>
            )}
            <button type="button" className="btn-secondary" onClick={onDigitar}>
              <IconKeyboard size={16} /> Digitar o código
            </button>
            <button type="button" className="btn-secondary" onClick={fechar}>
              Fechar a câmera
            </button>
          </div>
        </>
      )}
    </div>
  );
}
